import Box from '@amzn/meridian/box';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Profile } from '../../Types';
import Text from '@amzn/meridian/text';
import ProfileContainer from '../Dashboard/DashboardContainers/ProfileContainer';
import Row from '@amzn/meridian/row/row';
import TabGroup from '@amzn/meridian/tab/tab-group';
import Tab from '@amzn/meridian/tab/tab';
import SearchBanner from '../../commons/SearchBanner';
import PastContactsTable from './PastContactsTable';
import PastContactsFilters from './PastContactsFilters';
import { PastContactsTab } from './PastContactsTypes';
import useInspireOptions from '../../hooks/useInspireOptions';
import Pagination from '@amzn/meridian/pagination';
import Select, { SelectOption } from '@amzn/meridian/select';
import { pastContactsResultsPerPage } from './utils/PastContactsConstants';
import { usePaginatedPastContacts } from './hooks/usePaginatedPastContacts';
import PlaybackModal from '../ContactReview/Util/PlaybackModal';
import { usePlaybackModal } from './hooks/usePlaybackModal';
import { defaultCLClickstream } from './utils/ContactListeningHelpers';
import Column from '@amzn/meridian/column/column';

interface PastContactsHomeProps {
  profile: Profile;
  profileState: string;
}

const PastContactsHome: React.FC<PastContactsHomeProps> = ({
  profile,
  profileState,
}) => {
  const intl = useIntl();

  const { userID: requesterUserId, cscEntityPrefix } = useInspireOptions();

  // Modal state management via custom hook
  const {
    selectedContact,
    playbackModalOpen,
    contactListeningParams,
    handlePlayClick,
    setContactListeningParams,
    handleModalToggle,
  } = usePlaybackModal();

  const {
    currentTab,
    searchInput,
    showFilterSidebar,
    selectedChannel,
    selectedTimeOfDay,
    selectedDuration,
    selectedOwnership,
    contactsState,
    loading,
    timeOption,
    timestamp,
    weekValue,
    weekOptions,
    sortColumn,
    sortDirection,
    currentPage,
    itemPerPage,
    paginatedData,
    paginationMeta,
    columnWidths,
    showSicColumn,
    handleTabChange,
    handleSearchSubmit,
    handleToggleSidebars,
    handleSort,
    setSearchInput,
    setCurrentPage,
    setItemPerPage,
    handleBookmarkClick,
    setSelectedChannel,
    setSelectedTimeOfDay,
    setSelectedDuration,
    setSelectedOwnership,
    resetFilters,
    setTimeFilter,
    handleWeekChange,
  } = usePaginatedPastContacts(requesterUserId, cscEntityPrefix);

  const defaultItemsPerPage = 25;

  return (
    <>
      <Box height='100%'>
        <ProfileContainer
          state={profileState}
          profile={profile}
          selectedWeek={weekValue}
          isBig={true}
        />
        <Box spacingInset='none none 450'>
          <Text type='h500'>
            <FormattedMessage
              id='cs_perfportal_past_contacts'
              defaultMessage='Past contacts'
            />
          </Text>
        </Box>

        {/* Tab group section */}
        <Row spacingInset='none none 400'>
          <TabGroup value={currentTab} onChange={handleTabChange}>
            <Box spacingInset='none 350 none none'>
              <Tab value={PastContactsTab.ALL} key={PastContactsTab.ALL}>
                <FormattedMessage id='cs_perfportal_all' defaultMessage='All' />
              </Tab>
            </Box>
            <Tab
              value={PastContactsTab.BOOKMARKED}
              key={PastContactsTab.BOOKMARKED}
            >
              <FormattedMessage
                id='cs_perfportal_contact_table_tabname_bookmarked'
                defaultMessage='Bookmarked'
              />
            </Tab>
          </TabGroup>
        </Row>

        <Row alignmentVertical='top'>
          {/* Search bar + Past Contacts table section */}
          <Column alignmentVertical='top'>
            <SearchBanner
              searchValue={searchInput}
              onSearchValueChange={setSearchInput}
              onSearchSubmit={handleSearchSubmit}
              onToggleSidebars={handleToggleSidebars}
            />
            <Box spacingInset='300'>
              <PastContactsTable
                pastContacts={paginatedData}
                loading={loading}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                onBookmarkClick={handleBookmarkClick}
                columnWidths={columnWidths}
                showSicColumn={showSicColumn}
                onPlayClick={handlePlayClick}
              />
              {paginationMeta.visibleRecordsLen >= defaultItemsPerPage && (
                <Box width={'100%'} spacingInset='400 none none none'>
                  <Row widths='fill'>
                    <Row>
                      <Text>
                        <FormattedMessage
                          id='cs_perfportal_arc_pagination_setting'
                          defaultMessage='Results per page'
                        />
                      </Text>
                      <Select
                        value={itemPerPage}
                        onChange={setItemPerPage}
                        width={300}
                      >
                        {pastContactsResultsPerPage.map(resultsPerPage => {
                          return (
                            <SelectOption
                              value={resultsPerPage}
                              label={intl.formatMessage(
                                {
                                  id: 'cs_perfportal_arc_pagination_items',
                                  defaultMessage: '{itemCount} items',
                                },
                                { itemCount: resultsPerPage }
                              )}
                            />
                          );
                        })}
                      </Select>
                    </Row>
                    <Row alignmentHorizontal='end'>
                      <Pagination
                        showSkipArrows
                        numberOfPages={paginationMeta.numberOfPages}
                        onChange={setCurrentPage}
                        currentPage={currentPage}
                      />
                    </Row>
                  </Row>
                </Box>
              )}
            </Box>
          </Column>

          {/* Filters side bar section */}
          {showFilterSidebar && (
            <PastContactsFilters
              selectedChannel={selectedChannel}
              onChannelChange={setSelectedChannel}
              selectedTimeOfDay={selectedTimeOfDay}
              onTimeOfDayChange={setSelectedTimeOfDay}
              selectedDuration={selectedDuration}
              onDurationChange={setSelectedDuration}
              selectedOwnership={selectedOwnership}
              onOwnershipChange={setSelectedOwnership}
              selectedWeek={weekValue}
              weekOptions={weekOptions}
              onWeekChange={handleWeekChange}
              onResetFilters={resetFilters}
              onClose={handleToggleSidebars}
            />
          )}
        </Row>
      </Box>
      {selectedContact && (
        <PlaybackModal
          parentValue={selectedContact}
          setContactListeningParams={setContactListeningParams}
          CLClickstream={defaultCLClickstream}
          showButton={false}
          isOpen={playbackModalOpen}
          onToggle={handleModalToggle}
        />
      )}
    </>
  );
};

export default PastContactsHome;
